import { createTheme } from '@mui/material/styles';

// LiveOlympiad Brand Colors - Single Source of Truth
export const brandColors = {
  primary: '#5b21b6',     // Deep purple from branding gradient
  secondary: '#7c3aed',   // Mid purple from branding gradient  
  accent: '#8b5cf6',      // Light purple from branding gradient
  primaryHover: '#4c1d95', // Darker purple for hover states
  white: '#ffffff',
  textSecondary: 'rgba(255, 255, 255, 0.8)',
  background: '#FBF5F2',
  error: '#B00020',
  success: '#4CAF50',
  warning: '#FF9800',
  info: '#2196F3',
};

const theme = createTheme({
  palette: {
    primary: {
      main: brandColors.primary,
      contrastText: brandColors.white,
    },
    secondary: {
      main: brandColors.secondary,
      contrastText: brandColors.white,
    },
    background: {
      default: brandColors.background,
      paper: brandColors.white,
    },
    error: {
      main: brandColors.error,
    },
    success: {
      main: brandColors.success,
    },
    warning: {
      main: brandColors.warning,
    },
    info: {
      main: brandColors.info,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '10px 24px',
        },
        contained: {
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 50%, ${brandColors.accent} 100%)`,
          color: brandColors.white,
          border: 'none',
          boxShadow: '0 4px 12px rgba(91, 33, 182, 0.3)',
          '&:hover': {
            background: `linear-gradient(135deg, ${brandColors.primaryHover} 0%, ${brandColors.primary} 50%, ${brandColors.secondary} 100%)`,
            boxShadow: '0 6px 16px rgba(91, 33, 182, 0.4)',
          },
          '&:disabled': {
            background: '#ccc',
            color: brandColors.white,
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: brandColors.primary,
          color: brandColors.primary,
          '&:hover': {
            borderColor: brandColors.primaryHover,
            backgroundColor: 'rgba(91, 33, 182, 0.04)',
          },
        },
        text: {
          color: brandColors.primary,
          '&:hover': {
            backgroundColor: 'rgba(91, 33, 182, 0.04)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: 'rgba(91, 33, 182, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: brandColors.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brandColors.primary,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brandColors.white,
          color: brandColors.primary,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: brandColors.white,
          borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(91, 33, 182, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(91, 33, 182, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(91, 33, 182, 0.16)',
            },
          },
        },
      },
    },
  },
});

export default theme; 